import type { InferRequestType, InferResponseType } from 'hono/client';
import { useCallback } from 'react';
import { useSWRConfig } from 'swr';
import { downloadBase64File } from '@/hooks/file/file_download_helper';
import { type ApiClient, useApiClient, useFetch, useMutation } from '../api';

type GetDeviceFile =
  ApiClient['v2']['workspaces'][':workspaceId']['devices'][':deviceId']['files'][':fileId']['$get'];
export type DeviceFileDetailed = InferResponseType<GetDeviceFile, 200>;

type AddDeviceFile =
  ApiClient['v2']['workspaces'][':workspaceId']['devices'][':deviceId']['files']['$post'];
export type AddDeviceFileRequest = InferRequestType<AddDeviceFile>['json'];

const deviceFileCacheKey = (workspaceId: number, deviceId: number, fileId: number) =>
  `workspaces/${workspaceId}/devices/${deviceId}/file/${fileId}`;

export function useDeviceFile(deviceId: number, fileId: number, workspaceId: number) {
  return useFetch<DeviceFileDetailed>(deviceFileCacheKey(workspaceId, deviceId, fileId), (client) =>
    client.v2.workspaces[':workspaceId'].devices[':deviceId'].files[':fileId'].$get({
      param: {
        workspaceId: workspaceId.toString(),
        deviceId: deviceId.toString(),
        fileId: fileId.toString(),
      },
    })
  );
}

export function useAddDeviceFile(
  deviceId: number,
  workspaceId: number,
  actionOnErr: () => void = () => {}
) {
  return useMutation(
    `workspaces/${workspaceId}/devices/${deviceId}/files`,
    (client, updates: AddDeviceFileRequest) =>
      client.v2.workspaces[':workspaceId'].devices[':deviceId'].files.$post({
        param: { workspaceId: workspaceId.toString(), deviceId: deviceId.toString() },
        json: updates,
      }),
    actionOnErr
  );
}

export function useDeleteDeviceFile(
  deviceId: number,
  workspaceId: number,
  actionOnErr: () => void = () => {}
) {
  const { mutate } = useSWRConfig();
  const listCacheKey = `workspaces/${workspaceId}/devices/${deviceId}/files`;

  const deleteFileMutation = useMutation<void, number>(
    listCacheKey,
    (client, fileId: number) =>
      client.v2.workspaces[':workspaceId'].devices[':deviceId'].files[':fileId'].$delete({
        param: {
          workspaceId: workspaceId.toString(),
          deviceId: deviceId.toString(),
          fileId: fileId.toString(),
        },
      }),
    actionOnErr
  );

  return useCallback(
    async (fileId: number) => {
      await deleteFileMutation(fileId);
      await mutate(listCacheKey);
      await mutate(deviceFileCacheKey(workspaceId, deviceId, fileId));
      return true;
    },
    [deleteFileMutation, mutate, listCacheKey, workspaceId, deviceId]
  );
}

export function useDeviceFileDownload(
  workspaceId: number,
  deviceId: number,
  actionOnErr?: () => void
) {
  const client = useApiClient();

  return useCallback(
    async (fileId: number, fileName: string) => {
      try {
        const response = await client.v2.workspaces[':workspaceId'].devices[':deviceId'].files[
          ':fileId'
        ].$get({
          param: {
            workspaceId: workspaceId.toString(),
            deviceId: deviceId.toString(),
            fileId: fileId.toString(),
          },
        });

        if (response.status !== 200) {
          actionOnErr?.();
          return;
        }

        const deviceFile = await response.json();

        if (!deviceFile.fileContents || !downloadBase64File(deviceFile.fileContents, fileName)) {
          actionOnErr?.();
        }
      } catch {
        actionOnErr?.();
      }
    },
    [client, workspaceId, deviceId, actionOnErr]
  );
}
